/**
 * L1(b) Partner-46 (#822) — költségviselő-pool read-seam hook.
 * A pool a dedikált `GET /partners-direct/:partnerId/cost-bearers` endpointról jön
 * (pinnelt kontraktus, runtime Zod-parse a `getCostBearers` api-rétegben). A pool CSAK
 * UX-forrás — a bérlés pénz-célja a `Rental.costBearerId` explicit kijelölése marad.
 *
 * TanStack Query read-seam (a kgc-web bevett mintája): a `queryKey` a partnerId-hez
 * kötött, ezért partnerId-váltásnál a cache automatikusan vált — a régi partner poolja
 * NEM marad láthatóan kiválasztható (stale-data guard), és a lassú előző kérés eredménye
 * sem írja felül az újat (race guard, out-of-the-box). `enabled: !!partnerId` → partnerId
 * nélkül nem fetch-el és üres poolt ad.
 *
 * Tenant-scoped queryKey (Dexter second-opinion, money-adjacent defense-in-depth): a kulcs a
 * `tenantId`-t is tartalmazza, hogy elvi in-session tenant-váltáskor NE szivárogjon át
 * idegen-tenant költségviselő-pool a kliens-cache-ből (false-affordance-guard: a pultos ne
 * lásson/válasszon idegen-tenant költségviselőt a money-pinhez). A session=1-tenant invariánson
 * FELÜLI réteg — a szerver a `costBearerId`-write-nál úgyis JWT-tenantId-vel validál.
 */

import { getCostBearers, type CostBearerPoolEntry } from '@/api/partners';
import { useAuthStore } from '@/stores/auth-store';
import { useQuery } from '@tanstack/react-query';

export function useCostBearers(partnerId: string | undefined): {
  costBearers: CostBearerPoolEntry[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const tenantId = useAuthStore(s => s.user?.tenantId);
  const query = useQuery({
    queryKey: ['cost-bearers', tenantId, partnerId],
    queryFn: () => getCostBearers(partnerId as string),
    enabled: !!partnerId,
  });

  return {
    costBearers: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to fetch cost bearers'
      : null,
    refetch: async () => {
      await query.refetch();
    },
  };
}
